import { useCallback, useEffect, useState } from 'react';

export interface NumberRangeFilterValue {
  from?: number;
  to?: number;
}

export interface UseNumberRangeFilterParams {
  /**
   * The currently applied range coming from the parent state (e.g., URL/search params).
   * The hook keeps its local UI state in sync with these values.
   */
  applied: NumberRangeFilterValue;
  /** Called when the user applies the current local values. */
  onApply: (value: NumberRangeFilterValue) => void;
}

export interface UseNumberRangeFilterResult {
  /** Local UI state for the range inputs. */
  local: NumberRangeFilterValue;
  /** Updates local UI state (typically used by the input component). */
  onChange: (value: NumberRangeFilterValue) => void;
  /** Applies the current local values via `onApply`. */
  apply: () => void;
  /** Resets local UI state to empty (does not call `onApply`). */
  resetLocal: () => void;
}

/**
 * Shared helper hook for number range filters that need "local typing" + explicit "apply".
 *
 * Common use case: the screen stores applied filters in the URL, but the UI range inputs
 * should only update those filters when the user clicks an "Apply/OK" button.
 */
export const useNumberRangeFilter = ({
  applied,
  onApply,
}: UseNumberRangeFilterParams): UseNumberRangeFilterResult => {
  const [local, setLocal] = useState<NumberRangeFilterValue>({
    from: applied.from,
    to: applied.to,
  });

  // Keep local UI state in sync with externally applied values (e.g., when clearing filters).
  useEffect(() => {
    setLocal({
      from: applied.from,
      to: applied.to,
    });
  }, [applied.from, applied.to]);

  const handleChange = useCallback((value: NumberRangeFilterValue) => {
    setLocal(value);
  }, []);

  const apply = useCallback(() => {
    onApply(local);
  }, [local, onApply]);

  const resetLocal = useCallback(() => {
    setLocal({
      from: undefined,
      to: undefined,
    });
  }, []);

  return {
    local,
    onChange: handleChange,
    apply,
    resetLocal,
  };
};
